const STORAGE_KEY = 'bth-ticket-email-signature-v1';

export function loadTicketEmailSignature(): string {
  try {
    return sanitizeSignatureHtml(localStorage.getItem(STORAGE_KEY)?.trim() ?? '');
  } catch {
    return '';
  }
}

export function saveTicketEmailSignature(html: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, sanitizeSignatureHtml(html.trim()));
  } catch {
    /* ignore quota errors */
  }
}

function sanitizeSignatureHtml(html: string): string {
  if (!html) return html;
  return html
    .replace(/max-width\s*:\s*100%\s*;?/gi, '')
    .replace(/(<img\b[^>]*?)\sheight\s*=\s*["'][^"']*["']/gi, '$1');
}

export function buildDefaultTicketSignature(input: {
  displayName?: string;
  email?: string;
}): string {
  const name = input.displayName?.trim() || 'Ban Teck Han Support';
  const email = input.email?.trim();
  const lines = [
    `<p style="margin:0 0 4px;font-family:'Segoe UI',Arial,sans-serif;font-size:11pt;color:#333;">Best regards,</p>`,
    `<p style="margin:0 0 2px;font-family:'Segoe UI',Arial,sans-serif;font-size:11pt;color:#333;"><strong>${escapeHtml(name)}</strong></p>`,
    `<p style="margin:0;font-family:'Segoe UI',Arial,sans-serif;font-size:10pt;color:#666;">Ban Teck Han</p>`,
  ];
  if (email) {
    lines.push(
      `<p style="margin:4px 0 0;font-family:'Segoe UI',Arial,sans-serif;font-size:10pt;color:#666;">${escapeHtml(email)}</p>`,
    );
  }
  return lines.join('');
}

export function initialComposeBodyHtml(signatureHtml: string): string {
  const sig = signatureHtml.trim();
  if (!sig) return '<p></p>';
  return `<p></p><p></p>${sig}`;
}

export function stripEmptyComposeHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed || trimmed === '<p></p>' || /^<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>$/i.test(trimmed)) {
    return '';
  }
  return preserveEmailBlankLines(trimmed);
}

/** Outlook/HTML collapse empty <p></p> when margin is 0. Keep Enter blank lines visible. */
export function preserveEmailBlankLines(html: string): string {
  return html.replace(
    /<p(\b[^>]*)>(?:\s|&nbsp;|<br\b[^>]*>)*<\/p>/gi,
    (_match, attrs: string) => {
      const raw = attrs || '';
      if (/style="/i.test(raw)) {
        return `<p${raw}>&nbsp;</p>`;
      }
      return `<p${raw} style="margin:0;line-height:1.15;font-family:Calibri,'Segoe UI',sans-serif;font-size:11pt;">&nbsp;</p>`;
    },
  );
}

const MAX_SIGNATURE_IMAGE_BYTES = 350_000;
const MAX_SIGNATURE_IMAGE_WIDTH = 520;

/** Resize and embed an image as base64 for email signatures (Outlook-style inline HTML). */
export async function readSignatureImageDataUrl(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please choose a PNG, JPG, GIF, or WebP image.');
  }
  if (file.size > 4 * 1024 * 1024) {
    throw new Error('Image is too large. Use a file under 4 MB.');
  }

  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_SIGNATURE_IMAGE_WIDTH / Math.max(bitmap.width, 1));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not process image.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const preferPng = file.type.includes('png') || file.type.includes('gif');
  const mime = preferPng ? 'image/png' : 'image/jpeg';
  const quality = preferPng ? undefined : 0.88;

  let dataUrl = canvas.toDataURL(mime, quality);
  if (dataUrl.length > MAX_SIGNATURE_IMAGE_BYTES * 1.37 && !preferPng) {
    dataUrl = canvas.toDataURL('image/jpeg', 0.72);
  }
  if (dataUrl.length > MAX_SIGNATURE_IMAGE_BYTES * 1.37) {
    throw new Error('Image is still too large after resize. Try a smaller logo.');
  }
  return dataUrl;
}

export function signatureHtmlHasImages(html: string): boolean {
  return /<img\b/i.test(html);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
